import {
    Component, Input, forwardRef, ChangeDetectorRef, ChangeDetectionStrategy,
    OnInit, ViewChild, Output, EventEmitter, HostListener
} from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator, ControlValueAccessor, FormControl } from '@angular/forms';
import { ISelectOptions, SelectOptions } from '../../model/select.model';
import { get } from '../../../core/core';
import { valueType } from '../../model/lists.model';

const SELECT_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SelectComponent),
    multi: true
};

const VALIDATOR_ACCESSOR = {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => SelectComponent),
    multi: true,
}

@Component({
    selector: 'aria-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [SELECT_ACCESSOR, VALIDATOR_ACCESSOR],
})
export class SelectComponent implements OnInit, ControlValueAccessor, Validator {


    @Input() placeholder: string;
    @Input() showLabel: boolean = false;
    @Input() options: ISelectOptions;
    // initialize label field
    @Input() selectList: Array<any>;
    @Input() disabled: boolean;
    @Input() name: string;
    @Input() index: number = 1;
    @Output() select: EventEmitter<any> = new EventEmitter();
    @Input() ariaInvalid: boolean = false;

    optionsList: Array<any>;
    openSelect: boolean = false;
    selectedOption: any = {};
    isOptionSelected: boolean = false;
    label: string;

    @ViewChild('selectInput', { static: true }) selectInput: any;

    private _value: valueType;
    private _outputKey: string;
    private _labelKey: string;
    private _styleOnlyAuto: boolean;
    private _optionsFocusIndex: number = -1;

    constructor(private cdr: ChangeDetectorRef) {
        this.optionsList = [];
    }

    ngOnInit() {
        if (this.options) {
            this.options = new SelectOptions(this.options);
            this._outputKey = this.options.outputKey;
            this._labelKey = this.options.labelKey;
            this._styleOnlyAuto = this.options.styleOnlyAuto;
        }

        if (this.selectList && Array.isArray(this.selectList)) {
            this.optionsList = this.selectList;
        } else {
            this.selectList = null;
        }

    }

    //SETTERS / GETTERS

    get value(): any {
        return this._value;
    };

    set value(v: any) {
        if (v !== this._value) {
            this._value = v;

            this.cdr.markForCheck();
        }
    }

    @HostListener('keypress', ['$event']) onKeyPress(e) {
        e.preventDefault();
    }

    @HostListener('keydown', ['$event']) onKeyDown(e) {

        if (!this.openSelect || (this.openSelect && !this.optionsList.length)) return;
        if (e.key == 'Tab') {
            this.openSelect = false;
            return;
        }
        if (!/ArrowUp|ArrowDown/.test(e.key)) {
            return;
        };

        const keyName = e.key;

        if (this._optionsFocusIndex == -1) {
            this._optionsFocusIndex = 0;
        } else if (keyName == 'ArrowUp') {
            this._optionsFocusIndex--;
        } else if (keyName == 'ArrowDown') {
            if (this._optionsFocusIndex != this.optionsList.length - 1)
                this._optionsFocusIndex++;
        }

        if (this._optionsFocusIndex == -1) {
            this.selectInput.nativeElement.focus();
        } else {
            const currentElement = document.getElementById(`selectOpt-${this._optionsFocusIndex}`);
            if (currentElement) currentElement.focus();
        }
    }

    /**
     * Set value is used to setup output ngModel value
     * @param value string or number to change
     */
    setValue(value: valueType) {
        this.value = value;
        this.onChange(value)
    }

    /**
     * get value from select option by options outputKey
     * @param option option from select
     */
    getOptionValue(option: any) {
        return this._outputKey ? get(option, this._outputKey) : option;
    }

    /**
     * get label from select option by options labelKey
     * @param option option from select
     */
    getOptionLabel(option: any) {
        if (typeof option !== 'string') {
            return this._labelKey ? get(option, this._labelKey) : this.getOptionValue(option);
        } else {
            return option;
        }
    }

    /**
     * On model change is used to reset output value or to reset value from options list 
     * @param value  string or number inputed from user to change
     */
    onModelChange(value) {
        const option = this.optionsList.find((option) => this.getOptionLabel(option) === value);
        this.label = value;
        this.setValue(!!option ? this.getOptionValue(option) : null);

        this.selectedOption = this._styleOnlyAuto ? {} : option || {};

        this.selectInput.invalid = !option;

        this.emitSelection(option);

        this.cdr.markForCheck();
    }

    /**
     * Select option from select list
     * @param option abject or string to select
     */
    selectOption(option: any) {
        this.label = this.getOptionLabel(option);
        this.setValue(this.getOptionValue(option));
        this.selectedOption = this._styleOnlyAuto ? {} : option;
        this.openSelect = false;

        this.emitSelection(option);

        this.cdr.markForCheck();
    }

    /**
     * Emit selection in select envent
     * @param option current option selected
     */
    emitSelection(option: any) {
        if (this.isOptionSelected && !option) {
            this.isOptionSelected = false;
            this.select.emit({
                label: null,
                value: null,
                option: {}
            });
        }
        if (option) {
            this.isOptionSelected = true;
            this.select.emit({
                label: this.label,
                value: this.value,
                option: option
            });
        }
    }

    /**
     * On input focus open select or do the first call to retrieve data and than open select
     * @param value current value of input itself
     */
    onFocus(value) {
        this.openSelect = true;
        this._optionsFocusIndex = -1;
    }


    /**
     * On click outside autocomplete div or input close autocomplete
     * @param event click event
     */
    onBlur(event) {

        if (!event.relatedTarget || !/selectOpt-\d*/.test(event.relatedTarget.id)) {
            this.openSelect = false;
        }

    }


    // REQURED METHODS FOR NGMODEL DESIGN PATTERN

    public validate(c: FormControl) {
        let errors = { ...c.errors }, hasError = false;

        if (this.label && !this.value) {
            hasError = true;
            errors['noSelectionError'] = { valid: false }
        }

        if (hasError || c.invalid) {
            return errors;
        } else {
            return null;
        }
    }

    /**
     * Reuired from ngModel design pattern to work
     * @param value initial value
     */
    writeValue(value): void {
        if (String(value) !== String(this._value)) {
            this.label = value || typeof value == 'number' ? this.setInitialLabel(value) || value : '';
            this.setValue(value);
            this.cdr.markForCheck();
        }
    }

    setInitialLabel(value) {
        const option = this.optionsList.find((option) => this.getOptionValue(option) === value);
        return this.getOptionLabel(option);
    }

    onChange = (_) => { };
    onTouched = () => { };

    registerOnChange(fn: (_: any) => void): void { this.onChange = fn; }
    registerOnTouched(fn: () => void): void { this.onTouched = fn; }

}